import type { QueryClient } from '@tanstack/react-query'
import { pausePersist, resumePersist } from '@/lib/queryPersister'
import type { User } from '@/store/user.ts'

/**
 * Query key prefixes whose cached payload belongs to the caller's ACTIVE organization (or to a
 * stage of it) while the key itself carries no organization identity. Left in place across a
 * switch, React Query serves the organization we just left under the new one — and because the
 * cache is persisted to IndexedDB for 7 days, an offline read keeps doing so long afterwards.
 *
 * Removal is by PREFIX on purpose: `currStageUsersQuery` and `currStageIssueTypes` are keyed per
 * stage (e.g. ['currStageUsersQuery', {projectStageId, userId}]), so one prefix call drops every
 * stage variant instead of only the stage that happened to be active.
 *
 * The settings collections now carry their own identity (see `settingsQueryKeys`), so they can no
 * longer alias across organizations or projects on their own. They stay listed here anyway: their
 * payload still belongs to the organization being left, and dropping it keeps the switch from
 * showing a stale-but-correctly-keyed screen — and from carrying the old organization's data in
 * the 7-day IndexedDB snapshot. `orgProjectStages` and `getOrgMembers` still have no identity of
 * their own, so for them this purge is the only thing standing between a switch and a leak.
 */
export const ORGANIZATION_SCOPED_QUERY_PREFIXES: readonly string[] = [
  'userProjectParticipations',
  'currStageUsersQuery',
  'currStageIssueTypes',
  'organizationIssueTypes',
  'organizationStatusGroups',
  'organizationLabelGroups',
  'projectIssueTypes',
  'projectStatusGroups',
  'projectLabelGroups',
  'orgProjectStages',
  'getProjectStageParticipations',
  'getOrgMembers',
  // The four below carry their own identity, so they never ALIAS across organizations — but by
  // the same rule already applied to the settings collections above, their payload belongs to
  // the organization being left and must not ride along in the 7-day IndexedDB snapshot:
  //   'orgProjects'            -> ['orgProjects', orgId]             the org's stages incl. manager names
  //   'currentOrganization'    -> ['currentOrganization', {organizationId}]
  //                               organizationMemberships + invitedUsers: member names AND emails
  //   'invitedOrganizations'   -> [storeKey, orgId]                  cooperating-org member rosters
  //   'invitingOrganizations'  -> [storeKey, orgId]                  as above, the inviting side
  // The last two are keyed from a variable (organizationMembers.tsx picks the string by tab), so
  // grepping for a literal first element does not find them — they are real keys nonetheless.
  // 'project' is deliberately excluded: SETTINGS_VIEW permits a project participant to keep
  // viewing it after an organization switch, so purging it would destroy legitimate offline data.
  'orgProjects',
  'currentOrganization',
  'invitedOrganizations',
  'invitingOrganizations',
]

export type SwitchOrganizationDeps = {
  user: User | null
  /** IRI of the OrganizationMember row to activate, e.g. "/organization_members/173". */
  membershipIri: string
  queryClient: QueryClient
  patchActiveMembership: (userId: number, membershipIri: string) => Promise<unknown>
  fetchCurrentUser: () => Promise<User>
  /** Tear down SSE streams still bound to the previous organization's stage topics. */
  closeRealtimeConnections: () => void
  resetStageStore: () => void
  applyUser: (user: User) => void
  /** Must navigate somewhere valid for the new organization, or throw. */
  redirect: (user: User) => Promise<void> | void
  /** Re-run the offline prefetch, which otherwise stays "already done" for this session. */
  requestOfflinePrefetch: () => void
}

export type SwitchOrganizationResult =
  | { status: 'switched'; user: User }
  | { status: 'skipped'; reason: 'no-user' | 'already-active' }
  | { status: 'failed'; error: unknown; committed: boolean }

export async function purgeOrganizationScopedQueries(queryClient: QueryClient): Promise<void> {
  pausePersist()
  try {
    ORGANIZATION_SCOPED_QUERY_PREFIXES.forEach(prefix => {
      queryClient.removeQueries({ queryKey: [prefix] })
    })
  } finally {
    await resumePersist()
  }
}

/**
 * Move the user to another organization they are already a member of.
 *
 * Kept free of React so the ordering it guarantees can be pinned by a test:
 *  1. the server pointer moves FIRST — nothing local is disturbed if that fails;
 *  2. once it has moved the switch is COMMITTED, so every later failure still leaves the local
 *     state on the new organization (`committed: true`) rather than half-way;
 *  3. caches scoped to the old organization are dropped and old SSE streams closed before any
 *     new data is requested;
 *  4. the redirect runs against a re-fetched user, so stages resolve in the NEW organization.
 *
 * `activeProjectStage` is deliberately left alone: it still names a stage of the old
 * organization, `validateLastActiveStageId` rejects it and falls back to the first stage
 * available here, and loading that stage persists the new value.
 */
export async function switchOrganization(
  deps: SwitchOrganizationDeps,
): Promise<SwitchOrganizationResult> {
  const { user, membershipIri, queryClient } = deps

  if (!user?.id) return { status: 'skipped', reason: 'no-user' }
  if (user.activeMembership === membershipIri)
    return { status: 'skipped', reason: 'already-active' }

  try {
    await deps.patchActiveMembership(user.id, membershipIri)
  } catch (error) {
    // The pointer never moved: leave every cache and every stream intact.
    return { status: 'failed', error, committed: false }
  }

  await purgeOrganizationScopedQueries(queryClient)
  deps.closeRealtimeConnections()
  deps.resetStageStore()

  // The server is already on the new organization. Reflect that locally before anything else can
  // fail, so a dead network cannot leave the UI claiming the old organization is still active.
  deps.applyUser({ ...user, activeMembership: membershipIri })

  let freshUser: User
  try {
    freshUser = await deps.fetchCurrentUser()
    deps.applyUser(freshUser)
  } catch (error) {
    return { status: 'failed', error, committed: true }
  }

  try {
    await deps.redirect(freshUser)
  } catch (error) {
    return { status: 'failed', error, committed: true }
  }

  deps.requestOfflinePrefetch()
  return { status: 'switched', user: freshUser }
}
